import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import es from './locales/es.json';
import en from './locales/en.json';

/**
 * i18n para ManuSplit (patrón heredado de ManuJob).
 *
 * Locales soportados = 8:
 *   - Bundled (siempre en el bundle inicial): `es`, `en`.
 *   - Lazy (dynamic import cuando se selecciona): `gl`, `ca`, `eu`, `fr`, `de`,
 *     `pt`.
 *
 * Detección al arrancar: navigator.language.slice(0,2) → si es soportado, se
 * usa; el resto cae al fallback (`es` para hispanohablantes co-oficiales o `en`
 * para el mundo).
 *
 * Persistencia:
 * - Anonymous: localStorage (`manusplit_locale`), gestionado por el detector.
 * - Logueado no-demo: además `participants.preferred_locale`, y **sólo al
 *   elegir en Perfil**. Hasta el 2026-08-28 lo escribía un listener de
 *   `languageChanged` en `App.tsx`; se borró (#50) porque `changeLanguage` no
 *   dice quién lo llamó y también lo dispara el `.then()` de aquí abajo al
 *   cargar un bundle perezoso, con lo que un navegador en gallego dejaba `gl`
 *   escrito sin que nadie eligiera nada.
 * - Demo: solo localStorage; el sync desde BD se salta (ver App.tsx).
 *
 * Añadir un idioma: creas el JSON con las 610 claves, lo añades como bundled
 * (import estático arriba + `resources`) o como lazy (loader en LAZY_LOADERS +
 * entrada en AVAILABLE_LOCALES). Nada más.
 */

const ES_FALLBACK_LOCALES = ['va', 'oc'];
export const SUPPORTED_LOCALES = ['es', 'en', 'gl', 'ca', 'eu', 'fr', 'de', 'pt'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const BUNDLED: readonly SupportedLocale[] = ['es', 'en'];
const LAZY: readonly SupportedLocale[] = ['gl', 'ca', 'eu', 'fr', 'de', 'pt'];

const LAZY_LOADERS: Record<Exclude<SupportedLocale, 'es' | 'en'>, () => Promise<{ default: Record<string, unknown> }>> = {
  gl: () => import('./locales/gl.json'),
  ca: () => import('./locales/ca.json'),
  eu: () => import('./locales/eu.json'),
  fr: () => import('./locales/fr.json'),
  de: () => import('./locales/de.json'),
  pt: () => import('./locales/pt.json'),
};

// SVG banderas embebidos (heredados de ManuJob) — sin dependencias externas,
// funcionan offline y sobreviven a cualquier CSP.
const FLAG_ES =
  '<svg viewBox="0 0 20 14" width="20" height="14"><rect width="20" height="3.5" fill="#AA151B"/><rect y="3.5" width="20" height="7" fill="#F1BF00"/><rect y="10.5" width="20" height="3.5" fill="#AA151B"/></svg>';
const FLAG_EN =
  '<svg viewBox="0 0 20 14" width="20" height="14"><rect width="20" height="14" fill="#012169"/><path d="M0,0 L20,14 M20,0 L0,14" stroke="#fff" stroke-width="2.5"/><path d="M0,0 L20,14 M20,0 L0,14" stroke="#C8102E" stroke-width="1.5"/><path d="M10,0 V14 M0,7 H20" stroke="#fff" stroke-width="4"/><path d="M10,0 V14 M0,7 H20" stroke="#C8102E" stroke-width="2.5"/></svg>';
const FLAG_GL =
  '<svg viewBox="0 0 20 14" width="20" height="14"><rect width="20" height="14" fill="#fff"/><line x1="0" y1="14" x2="20" y2="0" stroke="#0055A4" stroke-width="3"/></svg>';
const FLAG_CA =
  '<svg viewBox="0 0 20 14" width="20" height="14"><rect width="20" height="14" fill="#FCDD09"/><g fill="#DA121A"><rect y="1.56" width="20" height="1.56"/><rect y="4.67" width="20" height="1.56"/><rect y="7.78" width="20" height="1.56"/><rect y="10.89" width="20" height="1.56"/></g></svg>';
const FLAG_EU =
  '<svg viewBox="0 0 20 14" width="20" height="14"><rect width="20" height="14" fill="#D52B1E"/><line x1="0" y1="0" x2="20" y2="14" stroke="#009B48" stroke-width="2"/><line x1="20" y1="0" x2="0" y2="14" stroke="#009B48" stroke-width="2"/><line x1="10" y1="0" x2="10" y2="14" stroke="#fff" stroke-width="2"/><line x1="0" y1="7" x2="20" y2="7" stroke="#fff" stroke-width="2"/></svg>';
const FLAG_FR =
  '<svg viewBox="0 0 20 14" width="20" height="14"><rect width="6.67" height="14" fill="#002395"/><rect x="6.67" width="6.67" height="14" fill="#fff"/><rect x="13.33" width="6.67" height="14" fill="#ED2939"/></svg>';
const FLAG_DE =
  '<svg viewBox="0 0 20 14" width="20" height="14"><rect width="20" height="4.67" fill="#000"/><rect y="4.67" width="20" height="4.67" fill="#DD0000"/><rect y="9.33" width="20" height="4.67" fill="#FFCC00"/></svg>';
// La esfera armilar va reducida a un circulo y sus dos bandas: a 20x14 el escudo
// real es una mancha, y lo que hay que poder decir de un vistazo es «verde y
// roja, con algo amarillo en medio». Misma simplificacion que la de Espana, que
// va sin escudo.
const FLAG_PT =
  '<svg viewBox="0 0 20 14" width="20" height="14"><rect width="8" height="14" fill="#006600"/><rect x="8" width="12" height="14" fill="#FF0000"/><circle cx="8" cy="7" r="3.2" fill="none" stroke="#FFFF00" stroke-width="1"/><path d="M4.8,7 H11.2 M8,3.8 V10.2" stroke="#FFFF00" stroke-width="0.8"/></svg>';

export interface LocaleDef {
  code: SupportedLocale;
  label: string;
  flag: string;
}

export const AVAILABLE_LOCALES: LocaleDef[] = [
  { code: 'es', label: 'Español', flag: FLAG_ES },
  { code: 'en', label: 'English', flag: FLAG_EN },
  { code: 'gl', label: 'Galego', flag: FLAG_GL },
  { code: 'ca', label: 'Català', flag: FLAG_CA },
  { code: 'eu', label: 'Euskara', flag: FLAG_EU },
  { code: 'fr', label: 'Français', flag: FLAG_FR },
  { code: 'de', label: 'Deutsch', flag: FLAG_DE },
  { code: 'pt', label: 'Português', flag: FLAG_PT },
];

function isSupported(code: string): code is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(code);
}

export function detectBrowserLocale(): SupportedLocale {
  if (typeof navigator === 'undefined') return 'es';
  const nav = navigator.language.slice(0, 2).toLowerCase();
  if (isSupported(nav)) return nav;
  if (ES_FALLBACK_LOCALES.includes(nav)) return 'es';
  return 'en';
}

/**
 * Normaliza cualquier código de locale al set soportado. Si `preferred_locale`
 * en BD trae un valor futuro o un ISO no soportado, cae con las mismas reglas
 * que `detectBrowserLocale`.
 */
export function normalizeToSupported(input: string | null | undefined): SupportedLocale {
  if (!input) return detectBrowserLocale();
  const base = input.slice(0, 2).toLowerCase();
  if (isSupported(base)) return base;
  if (ES_FALLBACK_LOCALES.includes(base)) return 'es';
  return 'en';
}

/**
 * Carga el resource bundle del locale si es lazy y aún no está cargado.
 * No-op para locales bundled o ya cargados. Idempotente.
 */
export async function ensureLocaleLoaded(code: SupportedLocale): Promise<void> {
  if ((BUNDLED as readonly string[]).includes(code)) return;
  if (i18n.hasResourceBundle(code, 'translation')) return;
  const loader = LAZY_LOADERS[code as Exclude<SupportedLocale, 'es' | 'en'>];
  if (!loader) return;
  const mod = await loader();
  i18n.addResourceBundle(code, 'translation', mod.default, true, true);
}

const initialLocale = detectBrowserLocale();

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      es: { translation: es },
      en: { translation: en },
    },
    fallbackLng: (SUPPORTED_LOCALES as readonly string[]).includes(initialLocale) ? initialLocale : 'es',
    supportedLngs: [...SUPPORTED_LOCALES],
    // Cuando falte una clave en un lazy locale (traducción parcial), i18next
    // debe caer a `es` — no al inglés — porque las 5 lazy son europeas que
    // conviven mejor con el castellano.
    fallbackNS: 'translation',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'manusplit_locale',
    },
  });

// Fallback en cascada para los lazy: si el locale actual no tiene la clave,
// prueba `es`, y si no, `en`. Mismo comportamiento que ManuJob.
i18n.options.fallbackLng = ['es', 'en'];

// Si el locale detectado es lazy, cargarlo y aplicarlo antes de que la UI
// renderice con el fallback.
if ((LAZY as readonly string[]).includes(initialLocale)) {
  void ensureLocaleLoaded(initialLocale).then(() => i18n.changeLanguage(initialLocale));
}

export default i18n;
